import { create } from '@bufbuild/protobuf'
import { type Timestamp } from '@bufbuild/protobuf/wkt'
import { type ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  type PlantMemberView,
  type PowerType,
  type PlantMasterListItem as ProtoPlantMasterListItem,
  type VoltageClass,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { listPlantMasters } from '@ebalance/proto/bffuser/v1/occto_master_service-OcctoMasterService_connectquery'
import {
  ListPlantMastersRequestSchema,
  type ListPlantMastersResponse,
} from '@ebalance/proto/bffuser/v1/occto_master_service_pb'
import { type Decimal } from '@ebalance/proto/google/type/decimal_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import { type CivilDate, requireCivilDate } from '~/models/civilDate'

// convertItem の引数は (aggregate RPC 由来の別メッセージ型とも構造的に
// 合わせるため) 手書きの構造型を使っており、 protoDateToCivilDate が要求する
// branded Message<"google.type.Date"> 型とは合致しない。 そのため zero-value
// 正規化ロジックのみをここでミラーする。
function plainDateToCivilDate(
  d: { year: number; month: number; day: number } | undefined,
): CivilDate | undefined {
  if (d === undefined || d.year === 0 || d.month === 0 || d.day === 0) {
    return undefined
  }
  return { year: d.year, month: d.month, day: d.day }
}

// Frontend display row type. Flattens optional proto fields into
// `undefined`-bearing primitives.
export type PlantMasterRow = {
  id: string
  plantCode: string
  plantName: string
  plantNameAbbr: string
  areaCode: AreaCode
  powerType: PowerType
  voltageClass: VoltageClass
  maxReceivingPowerKw: Decimal | undefined
  businessMasterId: string
  businessName: string
  contractIdNo2: string | undefined
  // AC-05/AC-06 (occto-master-audit-fix-bundle.md): 優先順位は任意 (未設定 = undefined)。
  priority: number | undefined
  proRataPriority: number | undefined
  note: string | undefined
  members: PlantMemberView[]
  /** この系統コードを使うユニットのうち、発電所のエリアが系統コードと違うもの (エリア不一致。backend が判定する)。 */
  areaMismatchedUnits: Array<{
    unitName: string
    siteName: string
    siteAreaCode: AreaCode
  }>
  updatedBy: string | undefined
  updatedAt: Timestamp | undefined
  /** 適用開始日 (必須)。 */
  validFrom: CivilDate
  validTo: CivilDate | undefined
}

function convertItem(
  item: {
    id: string
    plantCode: string
    plantName: string
    plantNameAbbr: string
    areaCode: AreaCode
    powerType: PowerType
    voltageClass: VoltageClass
    maxReceivingPowerKw?: Decimal
    businessMasterId: string
    contractIdNo2?: string
    priority?: number
    proRataPriority?: number
    note?: string
    members: PlantMemberView[]
    updatedBy?: string
    updatedAt?: Timestamp
    validFrom?: { year: number; month: number; day: number }
    validTo?: { year: number; month: number; day: number }
  },
  areaMismatchedUnits: PlantMasterRow['areaMismatchedUnits'],
): PlantMasterRow {
  // 代表事業者の businessName を members から取得する
  // 代表は isOwn フラグでなく businessMasterId と一致するメンバーで判定する (proto 設計)。
  const repMember = item.members.find(
    (m) => m.businessMasterId === item.businessMasterId,
  )
  const businessName = repMember?.businessName ?? ''

  return {
    id: item.id,
    plantCode: item.plantCode,
    plantName: item.plantName,
    plantNameAbbr: item.plantNameAbbr,
    areaCode: item.areaCode,
    powerType: item.powerType,
    voltageClass: item.voltageClass,
    maxReceivingPowerKw: item.maxReceivingPowerKw,
    businessMasterId: item.businessMasterId,
    businessName,
    contractIdNo2: item.contractIdNo2,
    priority: item.priority,
    proRataPriority: item.proRataPriority,
    note: item.note,
    members: item.members,
    areaMismatchedUnits,
    updatedBy: item.updatedBy,
    updatedAt: item.updatedAt,
    validFrom: requireCivilDate(
      plainDateToCivilDate(item.validFrom),
      `PlantMaster.validFrom (id=${item.id})`,
    ),
    validTo: plainDateToCivilDate(item.validTo),
  }
}

export function convertListPlantMastersResponse(
  resp: ListPlantMastersResponse,
): PlantMasterRow[] {
  // エリア不一致は一覧の応答の付随情報 (infos) にだけ載る。行ごとに id で引き、無ければ不一致なし
  const mismatches = new Map(
    resp.infos.map((info) => [
      info.plantMasterId,
      info.areaMismatchedUnits.map((unit) => ({
        unitName: unit.unitName,
        siteName: unit.siteName,
        siteAreaCode: unit.siteAreaCode,
      })),
    ]),
  )
  return resp.items.map((item) =>
    convertItem(item, mismatches.get(item.id) ?? []),
  )
}

export function useListPlantMasters(): UseQueryResult<
  ListPlantMastersResponse,
  ConnectError
> {
  return useQuery(listPlantMasters, create(ListPlantMastersRequestSchema, {}))
}

// PlantMasterListItemRow is the lightweight display row for the
// `GetGenerationSalesPlanMasterFormOptionsResponse.plants` projection
// (bffuser.v1.PlantMasterListItem, gen-sales-plant-master-select 設計書
// §5) — distinct from PlantMasterRow (= full ListPlantMasters detail).
export type PlantMasterListItemRow = {
  id: string
  plantCode: string
  plantName: string
  areaCode: AreaCode
  /** 配下の電源ユニットから決まる電源種別 (今日の値)。未定は UNSPECIFIED / undefined。 */
  powerType?: PowerType
}

/**
 * PlantMasterListItem[] (= 集約 RPC レスポンスの field 型) を
 * PlantMasterListItemRow[] に変換する。
 * GetGenerationSalesPlanMasterFormOptions は PlantMasterListItem[] を
 * 直接フィールドとして返すため、 convertListPlantMastersResponse (= 全件詳細)
 * の代わりに使う。
 */
export function convertPlantMasterListItems(
  items: ProtoPlantMasterListItem[],
): PlantMasterListItemRow[] {
  return items.map((item) => ({
    id: item.id,
    plantCode: item.plantCode,
    plantName: item.plantName,
    areaCode: item.areaCode,
    powerType: item.powerType,
  }))
}
